import React, { Suspense, type JSX } from "react"
import {
  $applyNodeReplacement,
  $createTextNode,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical"

import {
  findPlaceholderById,
  findPlaceholderByNormalizedValue,
  normalizeLiquid,
} from "@/components/lexical/placeholders"

import styles from "./styles.module.css"

const PlaceholderComponent = React.lazy(() => import("./component"))

const PLACEHOLDER_STYLES = {
  BASE: `${styles["placeholder-node"]} inline-flex items-center px-1.5 py-0 rounded bg-blue-background border border-blue-border-active text-blue-foreground font-medium whitespace-nowrap align-baseline text-sm`,
}

export const PLACEHOLDER_CLASS = PLACEHOLDER_STYLES.BASE

type SerializedPlaceholderNode = Spread<
  {
    text: string
    placeholderValue: string
    placeholderId: string
    description?: string
  },
  SerializedLexicalNode
>

function $convertPlaceholderElement(domNode: Node): DOMConversionOutput | null {
  if (domNode.nodeName !== "SPAN") return null

  const element = domNode as HTMLElement
  if (!element.hasAttribute("data-placeholder")) return null

  const rawValue = element.getAttribute("data-placeholder-value") ?? element.textContent ?? ""
  const id = element.getAttribute("data-placeholder-id")

  const definition = findPlaceholderById(id) ?? findPlaceholderByNormalizedValue(rawValue)
  if (!definition) {
    return { node: $createTextNode(rawValue) }
  }

  return { node: $createPlaceholderNode(definition.label, definition.value, definition.id, definition.description) }
}

export class PlaceholderNode extends DecoratorNode<JSX.Element> {
  __text: string
  __placeholderValue: string
  __placeholderId: string
  __description: string | undefined

  static getType(): string {
    return "placeholder"
  }

  static clone(node: PlaceholderNode): PlaceholderNode {
    return new PlaceholderNode(
      node.__text,
      node.__placeholderValue,
      node.__placeholderId,
      node.__description,
      node.__key
    )
  }

  constructor(text: string, placeholderValue: string, placeholderId: string, description?: string, key?: NodeKey) {
    super(key)
    this.__text = text
    this.__placeholderValue = placeholderValue
    this.__placeholderId = placeholderId
    this.__description = description
  }

  exportDOM(): DOMExportOutput {
    const textNode = document.createTextNode(this.__placeholderValue)
    return { element: textNode as unknown as HTMLElement }
  }

  static importDOM(): DOMConversionMap | null {
    return {
      // Conditional factory: returning null lets TextNode/ExtendedTextNode
      // handle Google Docs / Word inline-CSS spans (font-weight, etc.).
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-placeholder")) return null
        return {
          conversion: $convertPlaceholderElement,
          priority: 2,
        }
      },
    }
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span")
    span.style.display = "inline"
    return span
  }

  updateDOM(): false {
    return false
  }

  isInline(): boolean {
    return true
  }

  isKeyboardSelectable(): boolean {
    return false
  }

  decorate(): JSX.Element {
    return (
      <Suspense fallback={<span className={PLACEHOLDER_CLASS}>{this.__text}</span>}>
        <PlaceholderComponent nodeKey={this.__key} label={this.__text} description={this.__description} />
      </Suspense>
    )
  }

  // Override text content for plain text export
  getTextContent(): string {
    return this.__placeholderValue
  }

  exportJSON(): SerializedPlaceholderNode {
    return {
      ...super.exportJSON(),
      text: this.__placeholderValue,
      placeholderValue: this.__placeholderValue,
      placeholderId: this.__placeholderId,
      description: this.__description,
      type: "placeholder",
      version: 1,
    }
  }

  static importJSON(serializedNode: SerializedPlaceholderNode): PlaceholderNode {
    const definition =
      findPlaceholderById(serializedNode.placeholderId) ||
      findPlaceholderByNormalizedValue(serializedNode.placeholderValue) ||
      findPlaceholderByNormalizedValue(serializedNode.text)

    const value = definition?.value ?? serializedNode.placeholderValue ?? serializedNode.text
    const label = definition?.label ?? value
    const id = definition?.id ?? serializedNode.placeholderId ?? normalizeLiquid(value)
    const description = definition?.description ?? serializedNode.description

    return $createPlaceholderNode(label, value, id, description)
  }
}

export function $createPlaceholderNode(
  label: string,
  value: string,
  id: string,
  description?: string
): PlaceholderNode {
  return $applyNodeReplacement(new PlaceholderNode(label, value, id, description))
}

export function $isPlaceholderNode(node: LexicalNode | null | undefined): node is PlaceholderNode {
  return node instanceof PlaceholderNode
}
